import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Typography, TextField, Button, Box, Dialog, DialogContent, DialogContentText } from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import PdfViewer from './pdfviewer';
import axios from 'axios';
import * as common from '../../../commons/common';
import { Helmet } from 'react-helmet';
import { PlayCircle } from '@mui/icons-material';


const SafetyGuide = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { noticeId } = useParams();
    const formData = location.state;
    const [content, setContent] = useState({
        noticeNm: '',
        noticeFiles: []
    });
    const [isOpencompleteDialog, setIsOpencompleteDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0); // 현재 슬라이드 상태 추가
    const [isVideoOnly, setIsVideoOnly] = useState(false); //비디오 포맷만 있을 경우 UI 조정
    const [playingVideo, setPlayingVideo] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false); // 이미 제출 완료됐는지 추적
    const [confirmationKey, setConfirmationKey] = useState(''); // 제출 확인을 위한 고유 키

    useEffect(() => {
        window.history.pushState(null, document.title, window.location.href);
        if (formData == null || !noticeId) {
            navigate(`/safetyIntro/${noticeId}`);
        } else {
            fetchData();
            // 로컬 스토리지에서 해당 noticeId에 대한 제출 상태 확인
            const submittedKey = localStorage.getItem(`safety_guide_submitted_${noticeId}_${formData?.ssn}`);
            if (submittedKey) {
                setIsSubmitted(true);
            }
        }
    }, [formData, noticeId, navigate]);

    useEffect(() => {
        // 모든 파일이 비디오인지 확인
        const onlyVideoFiles = content.noticeFiles.every(
            file => file.fileFormat === 'video' || file.fileFormat === 'mp4'
        );
        setIsVideoOnly(onlyVideoFiles);
    }, [content.noticeFiles]);

    const fetchData = async () => {
        try {
            const response = await axios.get(`${common.getApiUrl()}/qr/notice/${noticeId}`, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.data) {
                const fetchedItems = {
                    noticeNm: response.data.noticeNm ?? '',
                    noticeFiles: response.data.noticeFiles ?? [],
                }
                setContent(fetchedItems);
            }
        } catch (error) {
            alert('오류가 발생하였습니다. 관리자에게 문의하세요');
        }
    };

    const handleStepChange = (step) => {
        setActiveStep(step); // 현재 슬라이드 변경 시 상태 업데이트
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // 이미 성공적으로 제출되었거나 현재 제출 중이면 중복 제출 방지
        if (isSubmitting || isSubmitted) {
            return;
        }

        const url = `${common.getApiUrl()}/notice/notice-confirm/${noticeId}`;

        try {
            setIsSubmitting(true); // 제출 시작 시 버튼 비활성화

            const requestData = {
                organizationNm: formData.organizationNm,
                noticeConfirmNm: formData.name,
                dateOfBirth: formData.ssn
            };

            const response = await axios.post(url, requestData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response) {
                // 성공 시 로컬 스토리지에 제출 상태 저장
                const submissionKey = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem(`safety_guide_submitted_${noticeId}_${formData.ssn}`, submissionKey);
                setConfirmationKey(submissionKey);
                setIsSubmitted(true);
                setIsOpencompleteDialog(true);
            }
        } catch (error) {
            alert(`Error: ${error.response?.data?.message || '오류가 발생했습니다.'}`);
            setIsSubmitting(false); // 에러 발생 시 버튼 다시 활성화
        }
    };

    const handleVideoClick = (videoPath) => {
        setPlayingVideo(videoPath);
    };

    const renderFile = (file) => {
        const fullPath = `${common.getImageBaseUrl()}${file.filePath}`;
        if (file.fileFormat === 'video' || file.fileFormat === 'mp4') {
            return (
                <Box sx={{ 
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', // 16:9 비율
                    backgroundColor: '#000',
                }}>
                    {playingVideo === fullPath ? (
                        <video
                            src={fullPath}
                            controls
                            autoPlay
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <Box
                            onClick={() => handleVideoClick(fullPath)}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .playIcon': {
                                        transform: 'scale(1.1)',
                                    }
                                }
                            }}
                        >
                            <video
                                src={fullPath}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                            <PlayCircle 
                                className="playIcon"
                                sx={{
                                    fontSize: '64px',
                                    color: 'white',
                                    opacity: 0.8,
                                    transition: 'transform 0.2s',
                                    zIndex: 1,
                                    filter: 'drop-shadow(0px 0px 8px rgba(0,0,0,0.5))'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            );
        } else if (file.fileFormat === 'pdf') {
            return <PdfViewer url={fullPath} />; // activeStep을 페이지로 설정
        } else if (file.fileFormat === 'jpg' || file.fileFormat === 'png') {
            return (
                <Box sx={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <img
                        src={fullPath}
                        alt={file.fileName}
                        style={{
                            maxWidth: '100%',
                            maxHeight: 'calc(100vh - 300px)', 
                            objectFit: 'contain'
                        }}
                    />
                </Box>
            );
        } else {
            return <Typography>지원하지 않는 파일 형식입니다.</Typography>;
        }
    };

    // 버튼 텍스트 및 스타일 결정
    const buttonText = isSubmitted ? '이수 완료됨' : (isSubmitting ? '처리중...' : '교육이수 확인');
    const buttonStyle = {
        backgroundColor: isSubmitted ? '#8bc34a' : 'var(--main-blue-color)',
        padding: '7px 35px',
        fontSize: '1.4rem',
        borderRadius: '30px',
        boxShadow: 'none',
        marginBottom: '20px',
        '&:disabled': {
            backgroundColor: isSubmitted ? '#8bc34a' : '#cccccc',
            color: isSubmitted ? 'white' : '#666666',
            opacity: isSubmitted ? 0.8 : 1
        }
    };

    return (
        <>
            <Helmet>
                <style>{'body { background-color: var(--main-white-color); }'}</style>
            </Helmet>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0 20px',
                    bgcolor: 'var(--main-white-color)'
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '20px', marginTop: isVideoOnly ? '80px' : '40px' }}>
                    공연장 안전수칙
                </Typography>
                <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Carousel
                        sx={{
                            flex: 1,
                            textAlign: 'center',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        indicators={false}
                        autoPlay={false}
                        navButtonsAlwaysInvisible={true}
                        index={activeStep}
                        onChange={(newIndex) => handleStepChange(newIndex)}
                        swipe={false}
                        touch={false}
                        animation="slide"
                    >
                        {content?.noticeFiles.map((file, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: '100%',
                                    // height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {activeStep === index && renderFile(file)}
                            </Box>
                        ))}
                    </Carousel>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', marginBottom: '30px' }}>
                    <Button
                        onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
                        disabled={activeStep === 0}
                    >
                        이전
                    </Button>
                    <Button
                        onClick={() => setActiveStep((prev) => Math.min(prev + 1, content.noticeFiles.length - 1))}
                        disabled={activeStep === content.noticeFiles.length - 1}
                    >
                        다음
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isSubmitted}
                    sx={buttonStyle}
                >
                    {buttonText}
                </Button>
                <Typography sx={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '20px' }}>
                    동영상 <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>시청 완료</span> 후<br />
                    반드시 교육이수 확인을 해주세요.
                </Typography>
                <Dialog
                    open={isOpencompleteDialog}
                    onClose={() => { }}
                    PaperProps={{
                        style: {
                            backgroundColor: '#424242',
                            color: 'white',
                            borderRadius: '10px',
                            width: '300px',
                            maxWidth: '500px',
                            marginTop: '-20vh',
                            boxShadow: 'none'
                        },
                    }}
                    disableEscapeKeyDown
                    disableBackdropClick
                >
                    <DialogContent>
                        <DialogContentText
                            sx={{ color: 'white', fontSize: '1.1rem', textAlign: 'center', boxShadow: 'none', wordBreak: 'keep-all', }}>
                            안전수칙 이수확인 등록이 완료되었습니다. <br />페이지를 닫아주시기 바랍니다.
                        </DialogContentText>
                    </DialogContent>
                </Dialog>
            </Box>
        </>
    );
};

export default SafetyGuide;
